import {StyledPagination, StyledPaginationItem, StyledRoot, carouselStyles, ctaBarStyles} from './style'
import {useTheme} from 'styled-components'
import {FC, useEffect, useState} from 'react'
import {MediaTypeE} from '@/types'
import HostProfileVerificationIcon from '../host-profile-verification-icon/HostProfileVerificationIcon'
import useWindowDimensions from '@/hooks/useWindowDimensions'
import {CtaBar} from '../cta-bar/CtaBar'
import {ProfileEventsPropsType} from '@/integrations/mixpanel/types'
import {useGetConnectedUser} from '@/hooks/useGetConnectedUser'
import {HostPartialType} from '@/features/authentication/types'
import {ProfileCarousel} from '@/components/ui/profile-carousel/ProfileCarousel'
import {Photos} from '../../helpers/helpers'
import noAvatar from '@/assets/images/noAvatar.png'
import {SubscriptionStatusesE} from '@/features/chat/chat.model'

type HostProfilePhotoSliderProps = {
    photos: Photos[]
    isGuest?: boolean
    isVerifiedHost?: boolean
    hostGenderId?: number
    hostName?: string
    setScrolledImages?: () => void
    host?: HostPartialType
    mixpanelData?: ProfileEventsPropsType
    handleSetEndTime?: () => void
    isInFeed?: boolean
}

export const HostProfilePhotoSlider: FC<HostProfilePhotoSliderProps> = ({
    photos,
    isGuest,
    isVerifiedHost,
    hostGenderId,
    hostName,
    setScrolledImages,
    host,
    mixpanelData,
    handleSetEndTime,
    isInFeed = false
}) => {
    const theme = useTheme()
    const {width} = useWindowDimensions()
    const [isMobile, setIsMobile] = useState(width <= 448)
    const [activeIndex, setActiveIndex] = useState(0)
    const {connectedUser: connectedHost} = useGetConnectedUser(host?.id)
    const subscriptionStatusActive =
        !!connectedHost && connectedHost?.metadata.subscription?.status === SubscriptionStatusesE.ACTIVE

    const BLOCKED_PHOTO_INDEX = 9

    useEffect(() => {
        setIsMobile(width <= 448)
    }, [width])

    const hostCardClosedRatio = 390 / 302
    const hostCardOpenRatio = 478 / 344

    const carouselOptions = {
        arrows: false,
        heightRatio: isMobile ? hostCardOpenRatio : hostCardClosedRatio,
        drag: !(photos?.length === 1)
    }

    const onSwipeCarousel = ({newIndex}: {newIndex: number}) => {
        setScrolledImages?.()
        setActiveIndex(newIndex)
    }

    const photosToRemap = photos
    const remappedMedia = photosToRemap?.length
        ? photosToRemap?.map(photo => ({
              type: MediaTypeE.IMAGE,
              url: photo?.url,
              created_at: photo?.created_at
          }))
        : [{type: MediaTypeE.IMAGE, url: noAvatar}]

    const isShowCta = subscriptionStatusActive || (!subscriptionStatusActive && !(activeIndex === BLOCKED_PHOTO_INDEX))

    const correctPhotosArray = isInFeed ? photos : remappedMedia

    return (
        <StyledRoot>
            <ProfileCarousel
                media={correctPhotosArray ?? []}
                carouselStyles={() => carouselStyles({theme, isInFeed})}
                customOptions={carouselOptions}
                withOverlay
                onSwipe={onSwipeCarousel}
                host={host}
                isGuest={isGuest}
                isInFeed={isInFeed}
                subscriptionStatusActive={subscriptionStatusActive}
            />
            {correctPhotosArray?.length > 1 && (
                <StyledPagination isInFeed={isInFeed} photoCount={correctPhotosArray?.length}>
                    {correctPhotosArray?.map((_, idx) => (
                        <StyledPaginationItem key={idx} isActive={idx === activeIndex} />
                    ))}
                </StyledPagination>
            )}
            {isVerifiedHost && <HostProfileVerificationIcon hostGenderId={hostGenderId} hostName={hostName} />}
            {isGuest && isShowCta && !isInFeed && (
                <CtaBar
                    host={host}
                    ctaBarStyles={() => ctaBarStyles({theme})}
                    mixpanelData={mixpanelData}
                    handleSetEndTime={handleSetEndTime}
                />
            )}
        </StyledRoot>
    )
}

HostProfilePhotoSlider.displayName = 'HostProfilePhotoSlider'
